import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import ChallengesList from './ChallengesList';
import ChallengeDetails from './ChallengeDetails';
import CreateChallengeDialog from './CreateChallengeDialog';
import { useChallenges } from '../../hooks/useChallenges';
import challengesApi from '../../services/challengesApi';

const ChallengesPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const { 
    challenges, 
    userChallenges,
    availableChallenges,
    loading, 
    error, 
    joinChallenge,
    leaveChallenge,
    fetchUserChallenges,
    fetchAvailableChallenges,
    refreshAllChallenges,
    setError 
  } = useChallenges();

  // Get current user
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { getCurrentUser } = await import('aws-amplify/auth');
        const user = await getCurrentUser();
        setCurrentUser(user.username);
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    };
    getCurrentUser();
  }, []);

  // Load user and available challenges when tab changes
  useEffect(() => {
    if (tabValue === 1 && userChallenges.length === 0) {
      fetchUserChallenges();
    } else if (tabValue === 2 && availableChallenges.length === 0) {
      fetchAvailableChallenges();
    }
  }, [tabValue, userChallenges.length, availableChallenges.length, fetchUserChallenges, fetchAvailableChallenges]);

  const showMessage = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleJoinChallenge = async (challengeId) => {
    try {
      await joinChallenge(challengeId);
      showMessage('Successfully joined the challenge!', 'success');
    } catch (error) {
      showMessage('Failed to join challenge. Please try again.', 'error');
    }
  };

  const handleLeaveChallenge = async (challengeId) => {
    if (!window.confirm('Are you sure you want to leave this challenge? Your progress will be saved but you won\'t be able to rejoin.')) {
      return;
    }

    try {
      await leaveChallenge(challengeId);
      showMessage('Successfully left the challenge.', 'success');
    } catch (error) {
      showMessage('Failed to leave challenge. Please try again.', 'error');
    }
  };

  const handleViewDetails = (challengeId) => {
    setSelectedChallenge(challengeId);
  };

  const handleCreateChallenge = async (challengeData) => {
    try {
      const newChallenge = await challengesApi.createChallenge(challengeData);
      showMessage('Challenge created successfully!', 'success');
      
      // Refresh challenges list
      refreshAllChallenges();
      
      return newChallenge;
    } catch (error) {
      console.error('Failed to create challenge:', error);
      throw error; // Let the dialog handle the error display
    }
  };

  const getCurrentChallenges = () => {
    switch (tabValue) {
      case 0:
        return challenges;
      case 1:
        return userChallenges;
      case 2:
        return availableChallenges;
      default:
        return challenges;
    }
  };

  const getTabLabel = (index) => {
    switch (index) {
      case 0:
        return `All Challenges ${challenges.length ? `(${challenges.length})` : ''}`;
      case 1:
        return `My Challenges ${userChallenges.length ? `(${userChallenges.length})` : ''}`;
      case 2:
        return `Available ${availableChallenges.length ? `(${availableChallenges.length})` : ''}`;
      default:
        return 'Challenges';
    }
  };

  if (selectedChallenge) {
    return (
      <Box sx={{ p: 3 }}>
        <ChallengeDetails 
          challengeId={selectedChallenge}
          onClose={() => setSelectedChallenge(null)}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Challenges
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Join challenges, compete with friends, and achieve your fitness goals
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />}
            onClick={refreshAllChallenges}
            disabled={loading}
          >
            Refresh
          </Button>
          
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Challenge
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={3}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading challenges...
          </Typography>
        </Box>
      )}

      {/* Tabs */}
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        sx={{ mb: 3 }}
        variant="fullWidth"
      >
        <Tab label={getTabLabel(0)} />
        <Tab label={getTabLabel(1)} />
        <Tab label={getTabLabel(2)} />
      </Tabs>

      {/* Tab Descriptions */}
      <Box sx={{ mb: 3 }}>
        {tabValue === 0 && (
          <Typography variant="body2" color="text.secondary">
            Browse all active challenges and join the ones that interest you
          </Typography>
        )}
        {tabValue === 1 && (
          <Typography variant="body2" color="text.secondary">
            View your joined challenges and track your progress
          </Typography>
        )}
        {tabValue === 2 && (
          <Typography variant="body2" color="text.secondary">
            Discover new challenges you can join
          </Typography>
        )}
      </Box>

      {/* Challenges List */}
      <ChallengesList
        challenges={getCurrentChallenges()}
        onJoin={handleJoinChallenge}
        onLeave={handleLeaveChallenge}
        onViewDetails={handleViewDetails}
        loading={loading}
        userChallenges={tabValue === 1}
        currentUser={currentUser}
      />

      {/* Create Challenge Dialog */}
      <CreateChallengeDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreateChallenge={handleCreateChallenge}
      />

      {/* Snackbar for messages */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChallengesPage;